import {
  DatePicker,
  Field,
  Input,
  InputNumber,
  RadioGroup,
  Select,
  Spin,
} from '@/components/commons';
import { FormField } from '@/components/commons/FormField';
import { ArticleService, OfferService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import {
  BannerSourceType,
  bannerSourceTypeJP,
  IGetDetailBannerResponse,
} from '@/types/response/banner';
import { Status, statusOptions } from '@/utils';
import { CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Image, Modal, Radio, Upload } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
interface IProps {
  isLoading: boolean;
  openDrawer: boolean;
  banner?: IGetDetailBannerResponse;
}

export const BannerForm: React.FC<IProps> = ({
  isLoading,
  openDrawer,
  banner,
}) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');

  const sourceType = watch('sourceType') as BannerSourceType | undefined;

  const sourceId = watch('sourceId');

  const status = watch('status');

  const { data: articlesData, isFetching: isFetchingArticles } = useQuery(
    [ENDPOINT.ARTICLE.GET_CHECKBOX_LIST, { bannerId: banner?.id }],
    () => ArticleService.getCheckboxList({ bannerId: banner?.id }),
    {
      enabled:
        (isModalOpen && sourceType === BannerSourceType.ARTICLE) ||
        (sourceType === BannerSourceType.ARTICLE && !!sourceId && openDrawer),
    },
  );

  const { data: offerData, isFetching: isFetchingOffers } = useQuery(
    [ENDPOINT.OFFER.GET_CHECKBOX_LIST, { bannerId: banner?.id }],
    () => OfferService.getCheckboxList({ bannerId: banner?.id }),
    {
      enabled:
        (isModalOpen && sourceType === BannerSourceType.OFFER) ||
        (sourceType === BannerSourceType.OFFER && !!sourceId && openDrawer),
    },
  );

  useEffect(() => {
    if (openDrawer) {
      setIsInitialLoad(true);
      const currentSourceId = watch('sourceId');
      setSelectedSourceId(currentSourceId || null);
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openDrawer]);

  const sourceTypeOptions = [
    {
      value: BannerSourceType.ARTICLE,
      label: bannerSourceTypeJP[BannerSourceType.ARTICLE],
    },
    {
      value: BannerSourceType.OFFER,
      label: bannerSourceTypeJP[BannerSourceType.OFFER],
    },
  ];

  const getModalData = () => {
    if (sourceType === BannerSourceType.ARTICLE) {
      return articlesData?.data || [];
    }
    if (sourceType === BannerSourceType.OFFER) {
      return offerData?.data || [];
    }

    return [];
  };

  const modalOptions = getModalData()
    .map((item: any) => ({
      ...item,
      value: Number(item?.id ?? item?.value),
      label: String(item?.name ?? item?.title ?? item?.label ?? ''),
      image:
        item?.imageCover?.url ??
        item?.image?.url ??
        item?.coverImageUrl ??
        item?.imageUrl ??
        null,
    }))
    .filter((option) => {
      if (!searchKeyword.trim()) return true;
      return option.label.toLowerCase().includes(searchKeyword.toLowerCase());
    });

  const handleModalOk = () => {
    if (selectedSourceId !== null) {
      // Kiểm tra xem selectedSourceId có tồn tại trong danh sách filtered không
      const isSelectedInFilteredList = modalOptions.some(
        (option) => option.value === selectedSourceId,
      );

      if (isSelectedInFilteredList) {
        setValue('sourceId', selectedSourceId, { shouldValidate: true });
        setIsModalOpen(false);
        setSearchKeyword('');
      }
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSearchKeyword('');
    const currentSourceId = watch('sourceId');
    setSelectedSourceId(currentSourceId || null);
  };

  const handleSourceTypeChange = (
    value: BannerSourceType | null | undefined,
  ) => {
    if (!value) {
      setValue('sourceType', undefined);
      setValue('sourceId', undefined);
      setSelectedSourceId(null);
      return;
    }
    setValue('sourceType', value, { shouldValidate: true });
    setValue('sourceId', undefined);
    setSelectedSourceId(null);
  };

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};

    const now = dayjs();
    const selectedDate = dayjs(current);

    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const disabledDateForEndDate = (current: Dayjs) => {
    const startAt = watch('startAt');
    if (!startAt) {
      return current.isBefore(dayjs().startOf('day'));
    }
    return current.isBefore(dayjs(startAt).startOf('day'));
  };

  const disabledTimeForEndDate = (current: Dayjs) => {
    if (!current) return {};

    const startAt = watch('startAt');
    if (!startAt) {
      return disabledTime(current);
    }

    const selectedDate = dayjs(current);
    const startDate = dayjs(startAt);

    if (selectedDate.isBefore(startDate, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(startDate, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < startDate.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === startDate.hour()) {
          const minutes = [];
          for (let i = 0; i <= startDate.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const image = watch('image');
  const attachments = watch('attachments');

  const handleUpload = (info: any) => {
    const file = info.file.originFileObj || info.file;
    const reader = new FileReader();
    reader.onload = () => {
      setValue('image', reader.result as string);
      setValue('attachments', info);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setValue('image', null);
    setValue('attachments', null);
  };

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');

    return true;
  };

  const isPublic = useMemo(
    () =>
      !!banner &&
      banner.status === Status.PUBLIC &&
      moment(banner.startAt).isSameOrBefore(moment()),
    [banner],
  );

  const isSchedule = useMemo(
    () =>
      !!banner &&
      banner.status === Status.PUBLIC &&
      moment(banner.startAt).isAfter(moment()),
    [banner],
  );

  const isDisabled = watch('isDisabled');

  useEffect(() => {
    if (isPublic && status === Status.PUBLIC && banner?.startAt) {
      setValue('startAt', moment(banner.startAt));
    }
  }, [status, isPublic, banner, setValue]);

  return (
    <>
      <Spin spinning={isLoading}>
        {!attachments?.fileList?.length && !image && (
          <Field
            label='バナー画像'
            name='attachments'
            className='mb-0'
            required
          >
            <div className='border-solid rounded border-2 border-zinc-300  w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                beforeUpload={beforeUpload}
                showUploadList={false}
                onChange={handleUpload}
                disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
              >
                {(!isPublic || status === Status.DRAFT) && (
                  <Button
                    type='default'
                    shape='default'
                    size='middle'
                    className='text-[#84ab42] border border-[#84ab42] font-semibold'
                  >
                    画像を選択
                  </Button>
                )}
              </Upload>
            </div>
          </Field>
        )}
        {image && openDrawer && !isLoading && (
          <Field label='バナー画像' name='image' className='mb-0' required>
            <div className='relative inline-block'>
              <Image
                height={100}
                src={image}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              {!isDisabled && (!isPublic || status === Status.DRAFT) && (
                <Button
                  type='primary'
                  shape='circle'
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={handleRemoveImage}
                  disabled={isPublic && status !== Status.DRAFT}
                  className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
                />
              )}
            </div>
          </Field>
        )}
        <div className='my-2 font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
        </div>
        <Field name='attachment' className='upload-field-error mb-0'>
          <div></div>
        </Field>
        <Field label='表示順' name='displayOrder' required>
          <InputNumber
            min={0}
            placeholder='表示順'
            className='bg-white'
            disabled={isDisabled}
          />
        </Field>
        <Field label='ソースタイプ' name='sourceType' required>
          <Select
            placeholder='ソースタイプを選択'
            style={{ width: '100%' }}
            options={sourceTypeOptions}
            onChange={handleSourceTypeChange}
            allowClear
            disabled={isDisabled}
          />
        </Field>
        <Field label='選択済みソース' name='sourceId' required>
          <div className='flex gap-2'>
            <Input
              placeholder='ソースを選択してください'
              value={
                modalOptions.find((opt) => opt.value === watch('sourceId'))
                  ?.label || ''
              }
              readOnly
              className='flex-1'
              disabled={isDisabled}
            />
            <Button
              type='default'
              onClick={() => {
                const currentSourceId = watch('sourceId');
                setSelectedSourceId(currentSourceId || null);
                setSearchKeyword('');
                setIsModalOpen(true);
              }}
              disabled={isDisabled || !sourceType}
            >
              選択
            </Button>
          </div>
        </Field>
        <label className='title-calendar font-semibold'>
          公開日時
          <Badge status='error' className='ml-2' />
        </label>
        <div className='flex items-baseline	gap-1 pt-2'>
          <Field name='startAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('startAt', e ? (e as Moment).second(0) : undefined);
                trigger('startAt');
                trigger('endAt');
              }}
              disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
            />
          </Field>
        </div>
        <label className='title-calendar font-semibold'>公開終了日時</label>
        <div className='flex items-baseline	gap-1 pt-2'>
          <Field name='endAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDateForEndDate}
              disabledTime={disabledTimeForEndDate}
              showTime
              onCalendarChange={(e) => {
                setValue('endAt', e ? (e as Moment).second(0) : undefined);
                trigger('endAt');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>
        <FormField label='公開ステータス' name='status' className='w-full'>
          <RadioGroup options={statusOptions} disabled={isDisabled} />
        </FormField>
      </Spin>
      <Modal
        title='ソースを選択'
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText='確定'
        cancelText='キャンセル'
        okButtonProps={{
          disabled:
            selectedSourceId === null ||
            !modalOptions.some((option) => option.value === selectedSourceId),
        }}
        width={600}
        loading={isFetchingArticles || isFetchingOffers}
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder='キーワードで検索'
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </div>
        <Radio.Group
          value={selectedSourceId}
          onChange={(e) => setSelectedSourceId(e.target.value)}
          style={{ width: '100%' }}
        >
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {modalOptions.length === 0 ? (
              <div
                style={{ textAlign: 'center', padding: '20px', color: '#999' }}
              >
                検索結果が見つかりません
              </div>
            ) : (
              modalOptions.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '12px',
                    padding: '8px',
                  }}
                  disabled={
                    option.publicEndAt &&
                    dayjs(option.publicEndAt).isBefore(dayjs())
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div className='flex-1'>
                      {option.image && (
                        <Image
                          src={option.image}
                          alt={option.label}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                          fallback={FALLBACK_URL}
                        />
                      )}
                    </div>
                    <span>{option.label}</span>
                  </div>
                </Radio>
              ))
            )}
          </div>
        </Radio.Group>
      </Modal>
    </>
  );
};
